/**
 * EngineRunDiff — Renders get_engine_run_diff response as a structured diff view.
 *
 * Uses design primitives:
 * - FinancialValue for money values
 * - MetricCard for metrics
 * - StatusBadge for statuses
 */

import React, { useState } from 'react';
import { GitCompare, ArrowRight, ChevronDown, ChevronUp, Play } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FinancialValue } from '../shared/FinancialValue';
import { StatusBadge } from '../shared/StatusBadge';
import { EngineRunDiffRequest, EngineRunDiffResult, DiffResult } from '../../hooks/useCopilot';

interface EngineRunDiffProps {
  reportId: string;
  onCompare: (request: EngineRunDiffRequest) => Promise<EngineRunDiffResult | null>;
  result: EngineRunDiffResult | null;
  isLoading: boolean;
}

const COMMON_ENGINES = [
  { id: 'fair_settlement', label: 'Fair Settlement' },
  { id: 'bottom_up', label: 'Bottom-Up' },
  { id: 'dual_method', label: 'Dual Method' },
  { id: 'enhanced_claim', label: 'Enhanced Claim' },
  { id: 'epi', label: 'EPI (Extended Period)' },
];

/**
 * Check if a value is a numeric diff result
 */
function isDiffResult(value: unknown): value is DiffResult {
  return (
    value !== null &&
    typeof value === 'object' &&
    'delta' in value &&
    'pct_change' in value &&
    'before' in value &&
    'after' in value
  );
}

/**
 * Recursively flatten diff object for display
 */
function flattenDiff(
  obj: Record<string, unknown> | null,
  prefix = ''
): Array<{
  key: string;
  label: string;
  path: string;
  value: unknown;
  isDiff: boolean;
  diff?: DiffResult;
}> {
  if (!obj) return [];

  const result: Array<{
    key: string;
    label: string;
    path: string;
    value: unknown;
    isDiff: boolean;
    diff?: DiffResult;
  }> = [];

  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const label = key
      .replace(/_/g, ' ')
      .replace(/\./g, ' › ')
      .replace(/\b\w/g, (l) => l.toUpperCase());

    if (isDiffResult(value)) {
      result.push({
        key,
        label,
        path,
        value,
        isDiff: true,
        diff: value,
      });
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result.push(...flattenDiff(value as Record<string, unknown>, path));
    }
  }

  return result;
}

/**
 * Get significance level for styling
 */
function getSignificance(pctChange: number): 'major' | 'minor' | 'none' {
  const abs = Math.abs(pctChange);
  if (abs >= 10) return 'major';
  if (abs >= 1) return 'minor';
  return 'none';
}

export function EngineRunDiff({ reportId, onCompare, result, isLoading }: EngineRunDiffProps) {
  const [engineId, setEngineId] = useState('');
  const [runIdBefore, setRunIdBefore] = useState('');
  const [runIdAfter, setRunIdAfter] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [useLatestTwo, setUseLatestTwo] = useState(true);

  const handleCompare = async () => {
    if (!engineId) return;
    await onCompare({
      reportId,
      engineId,
      ...(useLatestTwo ? {} : { runIdBefore, runIdAfter }),
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const diffItems = result ? flattenDiff(result.diff) : [];
  const hasResult = result && result.engine_id;

  return (
    <div className="space-y-4" data-testid="engine-diff">
      {/* Engine Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
          Engine
        </label>
        <select
          value={engineId}
          onChange={(e) => setEngineId(e.target.value)}
          className={cn(
            'w-full px-3 py-2 rounded-md text-sm',
            'bg-[var(--color-bg-elevated)] border border-[var(--color-border)]',
            'text-[var(--color-text-primary)]',
            'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]',
            'transition-colors'
          )}
          data-testid="diff-engine-select"
        >
          <option value="">Select an engine...</option>
          {COMMON_ENGINES.map((engine) => (
            <option key={engine.id} value={engine.id}>
              {engine.label}
            </option>
          ))}
        </select>
      </div>

      {/* Run Selection Mode */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={useLatestTwo}
            onChange={() => setUseLatestTwo(true)}
            className="accent-[var(--color-brand)]"
          />
          <span className="text-sm text-[var(--color-text-secondary)]">
            Compare two most recent runs
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={!useLatestTwo}
            onChange={() => setUseLatestTwo(false)}
            className="accent-[var(--color-brand)]"
          />
          <span className="text-sm text-[var(--color-text-secondary)]">
            Specify run IDs
          </span>
        </label>
      </div>

      {/* Specific Run IDs */}
      {!useLatestTwo && (
        <div className="space-y-3 p-3 rounded-md bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-muted)]">
              Before Run ID (optional)
            </label>
            <input
              type="text"
              value={runIdBefore}
              onChange={(e) => setRunIdBefore(e.target.value)}
              placeholder="Previous run ID"
              className={cn(
                'w-full px-3 py-2 rounded-md text-sm',
                'bg-[var(--color-bg-base)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]',
                'transition-colors'
              )}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-[var(--color-text-muted)]">
              After Run ID (optional)
            </label>
            <input
              type="text"
              value={runIdAfter}
              onChange={(e) => setRunIdAfter(e.target.value)}
              placeholder="Later run ID"
              className={cn(
                'w-full px-3 py-2 rounded-md text-sm',
                'bg-[var(--color-bg-base)] border border-[var(--color-border)]',
                'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]',
                'transition-colors'
              )}
            />
          </div>
        </div>
      )}

      {/* Compare Button */}
      <button
        onClick={handleCompare}
        disabled={!engineId || isLoading}
        className={cn(
          'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium',
          'bg-[var(--color-brand)] text-[var(--color-bg-base)]',
          'hover:bg-[var(--color-brand-hover)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors'
        )}
        data-testid="diff-compare-button"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Comparing...
          </>
        ) : (
          <>
            <GitCompare className="w-4 h-4" />
            Compare Runs
          </>
        )}
      </button>

      {/* Results */}
      {hasResult && (
        <div className="mt-6 space-y-4 border-t border-[var(--color-border)] pt-4" data-testid="diff-result">
          {/* Result Header */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-[var(--color-text-primary)]">
              Comparison Result
            </h4>
            <StatusBadge
              text={`${diffItems.length} changes`}
              variant={diffItems.length > 0 ? 'active' : 'neutral'}
              size="sm"
            />
          </div>

          {/* Run Info */}
          <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            {result.run_before ? (
              <>
                <span className="font-mono truncate max-w-[100px]">{result.run_before}</span>
                <ArrowRight className="w-3 h-3" />
                <span className="font-mono truncate max-w-[100px]">{result.run_after}</span>
              </>
            ) : (
              <span>Latest two runs compared</span>
            )}
          </div>

          {/* Diff Items */}
          {diffItems.length > 0 ? (
            <div className="space-y-2">
              {diffItems.map((item) => {
                if (!item.diff) return null;

                const significance = getSignificance(item.diff.pct_change);
                const isExpanded = expandedKeys.has(item.key);

                return (
                  <div
                    key={item.path}
                    className={cn(
                      'p-3 rounded-md border transition-colors',
                      significance === 'major'
                        ? 'bg-[var(--color-needs-human-tint)] border-[var(--color-needs-human-tint-bd)]'
                        : 'bg-[var(--color-bg-elevated)] border-[var(--color-border)]'
                    )}
                  >
                    <button
                      onClick={() => toggleExpanded(item.key)}
                      className="w-full flex items-center justify-between"
                    >
                      <span className="text-xs text-[var(--color-text-secondary)] truncate pr-2">
                        {item.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-xs font-mono font-medium',
                            item.diff.delta > 0
                              ? 'text-[var(--color-verified)]'
                              : item.diff.delta < 0
                                ? 'text-[var(--color-text-muted)]'
                                : 'text-[var(--color-text-dim)]'
                          )}
                        >
                          {item.diff.delta > 0 ? '+' : ''}
                          {item.diff.pct_change.toFixed(1)}%
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-3 h-3 text-[var(--color-text-muted)]" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-[var(--color-text-muted)]" />
                        )}
                      </div>
                    </button>

                    <div className="mt-2">
                      <FinancialValue value={item.diff.after} size="lg" />
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-[var(--color-border-sub)] space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[var(--color-text-muted)]">Before</span>
                          <FinancialValue value={item.diff.before} size="sm" />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[var(--color-text-muted)]">After</span>
                          <FinancialValue value={item.diff.after} size="sm" />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[var(--color-text-muted)]">Change</span>
                          <span
                            className={cn(
                              'text-sm font-mono',
                              item.diff.delta >= 0
                                ? 'text-[var(--color-verified)]'
                                : 'text-[var(--color-text-muted)]'
                            )}
                          >
                            {item.diff.delta >= 0 ? '+' : ''}
                            {item.diff.delta.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="text-[var(--color-text-muted)]">
                <GitCompare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No changes detected</p>
                <p className="text-xs mt-1">The runs produced identical outputs</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EngineRunDiff;
